/**
 * Admin Output Footer Sections API client — /api/admin-output-sections.php.
 * Credentialed (admin session). Backs the super-admin section designer.
 */

import type { OutputSection, OutputSectionContent } from '../config/outputSections';

const API_BASE = window.location.hostname === 'localhost' ? 'http://chriscme/api' : '/api';

export interface AdminOutputSection extends OutputSection {
  retailerId: number | null;
  /** Retailer rows only: this row removes the universal section with the same key. */
  isHidden: boolean;
}

export interface OutputSectionRetailerSummary {
  id: number;
  name: string;
  isPartner: boolean;
}

export interface OutputSectionsListResponse {
  sections: AdminOutputSection[];
  retailers: OutputSectionRetailerSummary[];
}

/** Writable payload (retailerId null = universal). key/retailerId are fixed after create. */
export interface OutputSectionPayload {
  retailerId: number | null;
  key: string;
  title: string;
  content?: OutputSectionContent;
  isHidden?: boolean;
  isActive?: boolean;
  sortOrder?: number;
}

async function handle<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || `Request failed (${res.status})`);
  }
  return res.json() as Promise<T>;
}

export async function fetchOutputSections(): Promise<OutputSectionsListResponse> {
  const res = await fetch(`${API_BASE}/admin-output-sections.php`, { credentials: 'include' });
  return handle<OutputSectionsListResponse>(res);
}

export async function createOutputSection(section: OutputSectionPayload): Promise<{ section: AdminOutputSection }> {
  const res = await fetch(`${API_BASE}/admin-output-sections.php`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ section }),
  });
  return handle(res);
}

export async function updateOutputSection(id: number, section: Partial<OutputSectionPayload>): Promise<{ section: AdminOutputSection }> {
  const res = await fetch(`${API_BASE}/admin-output-sections.php?id=${id}`, {
    method: 'PATCH',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ section }),
  });
  return handle(res);
}

/** Persist explicit sort orders for rows OWNED by a scope. */
export async function reorderOutputSections(retailerId: number | null, order: { id: number; sortOrder: number }[]): Promise<void> {
  const res = await fetch(`${API_BASE}/admin-output-sections.php?action=reorder`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ retailerId, order }),
  });
  await handle(res);
}

export async function deleteOutputSection(id: number): Promise<void> {
  const res = await fetch(`${API_BASE}/admin-output-sections.php?id=${id}`, {
    method: 'DELETE',
    credentials: 'include',
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || `Delete failed (${res.status})`);
  }
}
